/**
 * DeleteAccountPage.js
 * ---------------------
 * Página pública OBRIGATÓRIA para Google Play Data Safety.
 * URL: https://app.tectur.pt/apagar-conta
 *
 * Fluxo:
 *   1. Utilizador introduz email e motivo (opcional)
 *   2. Backend cria pedido em account_deletion_requests
 *   3. Admin processa em <=30 dias (SLA Google)
 *   4. Utilizador recebe confirmação por email
 *
 * A página aparece ANTES do login → também é linkada no Google Play Store
 * listing (campo "URL de eliminação de conta").
 */
import React, { useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeleteAccountPage() {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !confirm) return;
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/user/delete-account/request`, {
        email: email.trim().toLowerCase(),
        reason: (reason || '').trim(),
      });
      setResult(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Erro desconhecido');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <header style={styles.header}>
          <div style={styles.brand}>TECTUR 4X4</div>
          <h1 style={styles.title}>Eliminar Conta</h1>
        </header>

        {!result ? (
          <>
            <p style={styles.para}>
              Ao submeter este pedido, todos os dados associados à sua conta
              Tectur 4x4 serão <b>permanentemente eliminados</b> em até{' '}
              <b>30 dias</b>, incluindo:
            </p>
            <ul style={styles.list}>
              <li>Perfil, email e credenciais de acesso</li>
              <li>Histórico de rotas percorridas e favoritas</li>
              <li>Autocolantes (stamps do passaporte) conquistados</li>
              <li>Fotografias, comentários e avaliações submetidas</li>
              <li>Pertença a grupos privados e histórico de walkie-talkie</li>
              <li>Subscrição activa (será cancelada — ver reembolsos separadamente)</li>
            </ul>
            <p style={styles.paraWarn}>
              ⚠️ Esta acção é <b>irreversível</b>. Rotas partilhadas em grupos
              públicos podem manter-se anonimizadas para preservar contribuições
              agregadas da comunidade (agrupamento estatístico apenas).
            </p>

            <form onSubmit={submit} style={styles.form}>
              <label style={styles.label}>
                Email da conta a eliminar
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="o.teu.email@exemplo.pt"
                  data-testid="delete-account-email"
                  style={styles.input}
                />
              </label>
              <label style={styles.label}>
                Motivo (opcional)
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="Ajuda-nos a melhorar. O que te fez sair?"
                  data-testid="delete-account-reason"
                  style={styles.textarea}
                />
              </label>
              <label style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={confirm}
                  onChange={(e) => setConfirm(e.target.checked)}
                  data-testid="delete-account-confirm"
                />
                <span>
                  Confirmo que li e compreendo que todos os dados serão
                  eliminados de forma permanente.
                </span>
              </label>
              {error && (
                <div style={styles.error} data-testid="delete-account-error">
                  ⚠️ {error}
                </div>
              )}
              <button
                type="submit"
                disabled={busy || !confirm || !email}
                data-testid="delete-account-submit"
                style={{
                  ...styles.submitBtn,
                  ...(busy || !confirm || !email ? styles.submitBtnDisabled : {}),
                }}
              >
                {busy ? 'A submeter...' : 'ELIMINAR CONTA'}
              </button>
            </form>
          </>
        ) : (
          <div style={styles.successBox} data-testid="delete-account-success">
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.successTitle}>Pedido recebido</h2>
            <p style={styles.para}>{result.message}</p>
            <p style={styles.metaCode}>
              Ref: <code>{result.request_id}</code>
            </p>
            <p style={styles.paraSmall}>
              Guarda esta referência caso precises de contactar o suporte.
              Podes acompanhar o processo em <b>tectur.rotas@gmail.com</b>.
            </p>
          </div>
        )}

        <footer style={styles.footer}>
          <a href="https://app.tectur.pt/login" style={styles.footerLink}>
            ← Voltar ao início
          </a>
          <span style={styles.footerSep}>·</span>
          <a
            href="https://app.tectur.pt/privacy"
            style={styles.footerLink}
          >
            Política de Privacidade
          </a>
          <span style={styles.footerSep}>·</span>
          <a
            href="mailto:tectur.rotas@gmail.com"
            style={styles.footerLink}
          >
            Contactar suporte
          </a>
        </footer>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0F0D0B',
    color: '#F2EBDD',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '40px 16px',
    fontFamily: "'JetBrains Mono', monospace",
  },
  card: {
    maxWidth: 640,
    width: '100%',
    background: '#171310',
    border: '1px solid #3D362E',
    borderRadius: 8,
    padding: 32,
  },
  header: {
    borderBottom: '1px solid #3D362E',
    paddingBottom: 20,
    marginBottom: 24,
  },
  brand: {
    fontSize: 12,
    letterSpacing: 4,
    color: '#E87A2A',
    fontWeight: 900,
    marginBottom: 8,
  },
  title: {
    fontSize: 32,
    margin: 0,
    letterSpacing: 2,
    color: '#F2EBDD',
    textTransform: 'uppercase',
  },
  para: { fontSize: 14, lineHeight: 1.6, color: '#C4A275', margin: '12px 0' },
  paraSmall: { fontSize: 12, lineHeight: 1.5, color: '#8a7f6f', margin: '10px 0' },
  paraWarn: {
    fontSize: 13,
    lineHeight: 1.5,
    color: '#EF4444',
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.3)',
    padding: 12,
    borderRadius: 4,
    margin: '18px 0',
  },
  list: {
    fontSize: 13,
    color: '#C4A275',
    lineHeight: 1.8,
    paddingLeft: 20,
  },
  form: { display: 'flex', flexDirection: 'column', gap: 16, marginTop: 20 },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    fontSize: 11,
    letterSpacing: 1.5,
    color: '#8a7f6f',
    textTransform: 'uppercase',
    fontWeight: 800,
  },
  input: {
    padding: '10px 12px',
    background: '#0F0D0B',
    border: '1px solid #3D362E',
    borderRadius: 4,
    color: '#F2EBDD',
    fontSize: 14,
    fontFamily: 'inherit',
    outline: 'none',
  },
  textarea: {
    padding: '10px 12px',
    background: '#0F0D0B',
    border: '1px solid #3D362E',
    borderRadius: 4,
    color: '#F2EBDD',
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
    resize: 'vertical',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    fontSize: 12,
    color: '#C4A275',
    lineHeight: 1.5,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '14px 20px',
    background: '#EF4444',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 2,
    fontFamily: 'inherit',
    cursor: 'pointer',
    textTransform: 'uppercase',
    marginTop: 8,
  },
  submitBtnDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  error: {
    color: '#EF4444',
    fontSize: 12,
    padding: 10,
    background: 'rgba(239,68,68,0.08)',
    borderRadius: 4,
    border: '1px solid rgba(239,68,68,0.3)',
  },
  successBox: {
    textAlign: 'center',
    padding: '40px 20px',
  },
  successIcon: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: '#22C55E',
    color: '#0F0D0B',
    fontSize: 40,
    lineHeight: '72px',
    fontWeight: 900,
    margin: '0 auto 20px',
  },
  successTitle: {
    fontSize: 22,
    letterSpacing: 2,
    color: '#F2EBDD',
    margin: 0,
    marginBottom: 12,
    textTransform: 'uppercase',
  },
  metaCode: {
    fontSize: 12,
    color: '#8a7f6f',
    marginTop: 20,
  },
  footer: {
    borderTop: '1px solid #3D362E',
    paddingTop: 18,
    marginTop: 28,
    textAlign: 'center',
    fontSize: 11,
    letterSpacing: 1,
  },
  footerLink: {
    color: '#C4A275',
    textDecoration: 'none',
  },
  footerSep: {
    color: '#3D362E',
    margin: '0 8px',
  },
};
